import React from 'react';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Component to render field descriptions with support for HTML links
 * Converts <link> tags to clickable links that navigate using React Router
 * 
 * Usage in schema:
 * "Description": "Some text <link to='/path' style='color: red;'>click here</link> more text"
 */
const HtmlDescription = ({ description, variant = 'caption', color = 'text.secondary', sx = {} }) => {
  const navigate = useNavigate();

  if (!description) return null;

  // Parse the description for HTML tags including <link>, <strong>, <br/>, etc.
  const parseDescription = (text) => {
    // First, handle custom <link> tags
    const linkRegex = /<link\s+to=['"]([^'"]+)['"](?:\s+style=['"]([^'"]+)['"])?>(.*?)<\/link>/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }

      // Add the link
      parts.push({
        type: 'link',
        to: match[1],
        style: match[2] || 'color: #ef4444; text-decoration: underline; font-weight: 500; cursor: pointer;',
        content: match[3]
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    // If no custom links found, return the original text for general HTML rendering
    if (parts.length === 1 && parts[0].type === 'text') {
      return [{ type: 'html', content: text }];
    }

    return parts;
  };

  const parts = parseDescription(description);

  // Parse inline styles string to object
  const parseInlineStyles = (styleString) => {
    if (!styleString) return {};
    
    const styles = {};
    styleString.split(';').forEach(rule => {
      const [key, value] = rule.split(':').map(s => s.trim());
      if (key && value) {
        // Convert CSS property to camelCase (e.g., 'text-decoration' -> 'textDecoration')
        const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styles[camelKey] = value;
      }
    });
    return styles;
  };

  const handleLinkClick = (e, to) => {
    e.preventDefault();
    navigate(to);
  };

  return (
    <Typography variant={variant} color={color} sx={{ mt: 0.5, ...sx }} component="div">
      {parts.map((part, index) => {
        if (part.type === 'link') {
          const linkStyles = parseInlineStyles(part.style);
          return (
            <span
              key={index}
              onClick={(e) => handleLinkClick(e, part.to)}
              style={linkStyles}
            >
              {part.content}
            </span>
          );
        } else if (part.type === 'html') {
          // Use dangerouslySetInnerHTML for general HTML rendering
          return (
            <span 
              key={index}
              dangerouslySetInnerHTML={{ __html: part.content }}
            />
          );
        }
        return <span key={index}>{part.content}</span>;
      })}
    </Typography>
  );
};

export default HtmlDescription;
